<template>
    <div class="wtp-page">

        <!-- ===== This week: how far along the weekly goal is ===== -->
        <section class="wtp-card wtp-hero" :class="{ 'wtp-done': isWeekComplete }">
            <div class="wtp-cardHd">
                <h3>This Week Target</h3>
                <span class="wtp-range wtp-num">{{ currentWeekRange }}</span>
            </div>

            <div class="wtp-heroRow">
                <div class="wtp-ring">
                    <svg width="132" height="132" viewBox="0 0 132 132" aria-hidden="true">
                        <circle cx="66" cy="66" r="58" fill="none" stroke="#EFF1F4" stroke-width="13" />
                        <circle cx="66" cy="66" r="58" fill="none" :stroke="isWeekComplete ? '#2E9E57' : 'url(#wtpRing)'"
                            stroke-width="13" stroke-linecap="round" :stroke-dasharray="ringDash" />
                        <defs>
                            <linearGradient id="wtpRing" x1="0" y1="0" x2="132" y2="132" gradientUnits="userSpaceOnUse">
                                <stop stop-color="#FFB020" />
                                <stop offset="1" stop-color="#F2820D" />
                            </linearGradient>
                        </defs>
                    </svg>
                    <div class="wtp-ringLabel">
                        <span class="wtp-ringPct wtp-num">{{ weekPercent }}<i>%</i></span>
                        <span class="wtp-cap">Complete</span>
                    </div>
                </div>

                <div class="wtp-heroFigures">
                    <div>
                        <div class="wtp-cap">Streamed</div>
                        <div class="wtp-bigTime wtp-num">{{ workingHoursTime }}</div>
                        <!-- The weekly goal is per-host: 8 hrs for some, 42 for others. -->
                        <div class="wtp-ofTarget">of {{ hoursLabel(CurrentWeekTargetData.total_hours) }} target</div>
                    </div>
                    <div class="wtp-rule"></div>
                    <div>
                        <div class="wtp-cap">{{ isWeekComplete ? 'Status' : 'Still to go' }}</div>
                        <div class="wtp-leftTime wtp-num">{{ isWeekComplete ? 'Target complete' : remainingTime }}</div>
                    </div>
                </div>
            </div>

            <div class="wtp-note" v-if="perDayTarget > 0">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F2820D" stroke-width="2.2"
                    stroke-linecap="round">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5.2l3.2 1.9" />
                </svg>
                <span>Up to {{ hoursLabel(perDayTarget) }} counts towards the target each day</span>
            </div>
        </section>

        <!-- ===== The week, one column per day ===== -->
        <section class="wtp-card">
            <div class="wtp-cardHd">
                <h3>Day by day</h3>
                <span class="wtp-sub" v-if="perDayTarget > 0">{{ hoursLabel(perDayTarget) }} max</span>
            </div>
            <div class="wtp-days">
                <div v-for="day in currentDays" :key="day.name" class="wtp-day" :class="{
                    'is-today': day.isToday,
                    'is-hit': day.hit,
                    'is-empty': !day.seconds
                }">
                    <span class="wtp-dayVal wtp-num">{{ day.short }}</span>
                    <div class="wtp-dayTrack">
                        <div class="wtp-dayFill" :style="{ height: day.fillPercent + '%' }"></div>
                    </div>
                    <span class="wtp-dayName">{{ day.name }}</span>
                    <span v-if="showPeak" class="wtp-dayPeak wtp-num" :class="{ 'is-noPeak': !day.peakSeconds }">
                        {{ day.peakShort }}
                    </span>
                </div>
            </div>
        </section>

        <!-- ===== Peak hour bonus (status 3 hosts only) ===== -->
        <section class="wtp-card" v-if="showPeak">
            <div class="wtp-peakHd">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#7C40FF" stroke-width="2.2"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M13 2.5L4.5 13.5H11l-1 8 8.5-11H12l1-7.5z" />
                </svg>
                <h3>Peak Hour Bonus</h3>
            </div>
            <p class="wtp-peakNote">
                Hours streamed during peak slots count again here, on top of your weekly target.
                Up to {{ hoursLabel(perDayPeakTarget) }} a day.
            </p>
            <div class="wtp-peakFigures">
                <span class="wtp-peakDone wtp-num">{{ peak_workingHoursTime }}</span>
                <span class="wtp-peakOf wtp-num">of {{ hoursLabel(CurrentWeekTargetData.total_peak_hours) }}</span>
            </div>
            <div class="wtp-bar">
                <div class="wtp-barFill" :style="{ width: peakBarWidth + '%' }"></div>
            </div>
        </section>

        <!-- ===== Last week ===== -->
        <section class="wtp-card">
            <div class="wtp-cardHd">
                <h3>Last Week Target</h3>
                <span class="wtp-range wtp-num">{{ lastWeekRange }}</span>
            </div>

            <div class="wtp-reward" :class="{ 'is-paid': totalReward > 0 }">
                <div>
                    <div class="wtp-cap">Reward earned</div>
                    <div class="wtp-rewardVal wtp-num">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <circle cx="12" cy="12" r="9.5" fill="#F7C948" />
                            <circle cx="12" cy="12" r="6.4" fill="none" stroke="#E0A21B" stroke-width="1.5" />
                        </svg>
                        <span>{{ formatNumber(totalReward) }}</span>
                    </div>
                </div>
                <div class="wtp-streamed">
                    <div class="wtp-cap">Streamed</div>
                    <div class="wtp-streamedVal wtp-num">{{ lastWeek_workingHoursTime }}</div>
                </div>
            </div>

            <p class="wtp-missed" v-if="totalReward === 0 && (LastWeekTargetData.total_hours || 0) > 0">
                No reward this time — {{ hoursLabel(LastWeekTargetData.total_hours) }} were needed and
                {{ lastWeek_workingHoursTime }} were streamed.
            </p>

            <button type="button" class="wtp-toggle" :class="{ 'is-open': showLastWeekReport }"
                @click="showLastWeekReport = !showLastWeekReport">
                <span>{{ showLastWeekReport ? 'Hide time report' : 'Show time report' }}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M6 9.5l6 6 6-6" />
                </svg>
            </button>

            <div class="wtp-days wtp-lastDays" v-if="showLastWeekReport">
                <div v-for="day in lastWeekDays" :key="day.name" class="wtp-day" :class="{
                    'is-hit': day.hit,
                    'is-empty': !day.seconds
                }">
                    <span class="wtp-dayVal wtp-num">{{ day.short }}</span>
                    <div class="wtp-dayTrack">
                        <div class="wtp-dayFill" :style="{ height: day.fillPercent + '%' }"></div>
                    </div>
                    <span class="wtp-dayName">{{ day.name }}</span>
                    <span v-if="showPeak" class="wtp-dayPeak wtp-num" :class="{ 'is-noPeak': !day.peakSeconds }">
                        {{ day.peakShort }}
                    </span>
                </div>
            </div>
        </section>

        <!-- ===== Policy ===== -->
        <router-link v-if="policyRoute" class="wtp-card wtp-policy" :to="policyRoute">
            <span class="wtp-policyIcon">
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#2F6FE4" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M5 4.5h9.5L19 9v10.5H5z" />
                    <path d="M14 4.5V9h5" />
                    <path d="M8.5 13h7" />
                    <path d="M8.5 16.5h4.5" />
                </svg>
            </span>
            <span class="wtp-policyText">
                <b>Weekly Target Policy</b>
                <span>How hours and rewards are counted</span>
            </span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8b95a4" stroke-width="2.2"
                stroke-linecap="round" stroke-linejoin="round">
                <path d="M9.5 5l7 7-7 7" />
            </svg>
        </router-link>

    </div>
</template>

<script>
import axios from 'axios';
import './WeeklyTargetRefresh.css';

// Mon-first, because the target week runs Monday to Sunday.
const DAYS = [
    { key: 'monday', name: 'Mon' },
    { key: 'tuesday', name: 'Tue' },
    { key: 'wednesday', name: 'Wed' },
    { key: 'thursday', name: 'Thu' },
    { key: 'friday', name: 'Fri' },
    { key: 'saturday', name: 'Sat' },
    { key: 'sunday', name: 'Sun' },
];

const RING_RADIUS = 58;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export default {
    name: 'WeeklyTarget',
    data() {
        return {
            CurrentWeekTargetData: {}, // object expected from API
            LastWeekTargetData: {},
            statusVal: 1,
            token: '',

            currentWeekRange: '',
            lastWeekRange: '',

            workingHoursTime: '',
            remainingTime: '',
            peak_workingHoursTime: '',
            peak_remainingTime: '',

            lastWeek_workingHoursTime: '',
            lastWeek_remainingTime: '',
            lastWeek_peak_workingHoursTime: '',
            lastWeek_peak_remainingTime: '',

            showLastWeekReport: false,
        };
    },

    computed: {
        // Peak hours are only part of the 42 hrs programme (status 3), and only
        // when the API actually sends a peak target.
        showPeak() {
            return this.statusVal === 3 && (this.CurrentWeekTargetData.total_peak_hours || 0) !== 0;
        },

        // per-day targets from API
        perDayTarget() {
            return this.CurrentWeekTargetData.target_per_day_hours || 0;
        },
        perDayPeakTarget() {
            return this.CurrentWeekTargetData.target_per_day_peak_hours || 0;
        },
        perDayTargetLast() {
            return this.LastWeekTargetData.target_per_day_hours || 0;
        },

        // Mon = 0 … Sun = 6, so the chart and getDay() line up. Matching on the
        // day letter used to mark both Tuesday and Thursday as "today".
        todayIndex() {
            return (new Date().getDay() + 6) % 7;
        },

        currentDays() {
            return this.buildDays(this.CurrentWeekTargetData, this.perDayTarget, this.todayIndex);
        },

        lastWeekDays() {
            return this.buildDays(this.LastWeekTargetData, this.perDayTargetLast, -1);
        },

        // progress computations (safe with defaults)
        currentWeek_progressPercentage() {
            return this.percentOf(this.CurrentWeekTargetData.working_hours, this.CurrentWeekTargetData.total_hours);
        },
        current_peakWeek_progressPercentage() {
            return this.percentOf(this.CurrentWeekTargetData.working_peak_hours, this.CurrentWeekTargetData.total_peak_hours);
        },
        lastWeek_progressPercentage() {
            return this.percentOf(this.LastWeekTargetData.working_hours, this.LastWeekTargetData.total_hours);
        },
        lastWeek_peakWeek_progressPercentage() {
            return this.percentOf(this.LastWeekTargetData.working_peak_hours, this.LastWeekTargetData.total_peak_hours);
        },

        weekPercent() {
            return Math.round(this.currentWeek_progressPercentage);
        },

        isWeekComplete() {
            return this.currentWeek_progressPercentage >= 100;
        },

        ringDash() {
            const filled = (this.currentWeek_progressPercentage / 100) * RING_CIRCUMFERENCE;
            return `${filled.toFixed(1)} ${RING_CIRCUMFERENCE.toFixed(1)}`;
        },

        // A sliver of colour so "started but barely" never looks like zero.
        peakBarWidth() {
            const pct = this.current_peakWeek_progressPercentage;
            return pct > 0 ? Math.max(pct, 3) : 0;
        },

        totalReward() {
            const reward = this.LastWeekTargetData.reward || 0;
            const peakReward = this.LastWeekTargetData.peak_reward || 0;
            return reward + peakReward;
        },

        policyRoute() {
            // statusVal defaults to 1, so without this the very first render
            // built "/weeklytarget_policy/token=" with no token and the router
            // logged "No match found for location".
            if (!this.token) return null;
            if (this.statusVal === 1) return `/weeklytarget_policy/token=${this.token}`;
            if (this.statusVal === 3) return `/weeklytarget_policy_status3/token=${this.token}`;
            return null;
        },
    },

    mounted() {
        this.setWeekDates();
        this.fetchweeklyTargetData();
    },
    watch: {
        '$route.params.token': 'fetchweeklyTargetData'
    },

    methods: {
        setWeekDates() {
            const now = new Date();
            const day = now.getDay();
            const diffToMonday = day === 0 ? -6 : 1 - day;

            const currentMonday = new Date(now);
            currentMonday.setDate(now.getDate() + diffToMonday);
            currentMonday.setHours(0, 0, 0, 0);

            const currentSunday = new Date(currentMonday);
            currentSunday.setDate(currentMonday.getDate() + 6);

            const lastMonday = new Date(currentMonday);
            lastMonday.setDate(currentMonday.getDate() - 7);

            const lastSunday = new Date(currentSunday);
            lastSunday.setDate(currentSunday.getDate() - 7);

            this.currentWeekRange = `${this.formatDay(currentMonday)} – ${this.formatDay(currentSunday)}`;
            this.lastWeekRange = `${this.formatDay(lastMonday)} – ${this.formatDay(lastSunday)}`;
        },

        // "07 Sep" — short enough to sit next to a card heading.
        formatDay(date) {
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${String(date.getDate()).padStart(2, '0')} ${months[date.getMonth()]}`;
        },

        fetchweeklyTargetData() {
            // read token safely
            const tokenParam = this.$route.params.token || this.$route.query.token || '';
            this.token = typeof tokenParam === 'string' ? tokenParam.replace(/^token=/, '') : tokenParam;

            axios.get(`api/freetarget-weekly`, {
                headers: {
                    Authorization: `Bearer ${this.token}`
                }
            })
                .then(response => {
                    const res = response.data.result || {};
                    this.CurrentWeekTargetData = res.current_week_data || {};
                    this.LastWeekTargetData = res.last_week_data || {};
                    this.statusVal = res.status || 1;

                    // compute and format times
                    this.remainingSeconds = (this.CurrentWeekTargetData.total_hours || 0) - (this.CurrentWeekTargetData.working_hours || 0);
                    this.peak_remainingSeconds = (this.CurrentWeekTargetData.total_peak_hours || 0) - (this.CurrentWeekTargetData.working_peak_hours || 0);

                    this.updateRemainingTime();
                    this.updateWorkingTimeFormate();
                    this.peak_updateRemainingTime();
                    this.peak_updateWorkingTimeFormate();

                    this.lastWeek_remainingSeconds = (this.LastWeekTargetData.total_hours || 0) - (this.LastWeekTargetData.working_hours || 0);
                    this.lastWeek_peak_remainingSeconds = (this.LastWeekTargetData.total_peak_hours || 0) - (this.LastWeekTargetData.working_peak_hours || 0);

                    this.lastWeek_updateRemainingTime();
                    this.lastWeek_updateWorkingTimeFormate();
                    this.lastWeek_peak_updateRemainingTime();
                    this.lastWeek_peak_updateWorkingTimeFormate();
                })
                .catch(error => {
                    console.error("Error Fetching weekly target data", error);
                });
        },

        // One column per day, sized against that week's own per-day cap so an
        // 8 hrs host and a 42 hrs host both get a full-looking chart.
        buildDays(source, perDayCap, todayIndex) {
            const data = source || {};
            return DAYS.map((day, index) => {
                const seconds = data[day.key] || 0;
                const peakSeconds = data[`${day.key}_peak`] || 0;
                const ratio = perDayCap > 0 ? Math.min(1, seconds / perDayCap) : 0;
                return {
                    name: day.name,
                    seconds,
                    peakSeconds,
                    short: this.compactTime(seconds),
                    peakShort: this.compactTime(peakSeconds),
                    hit: perDayCap > 0 && seconds >= perDayCap,
                    // A day with any time on it keeps a visible stub.
                    fillPercent: seconds ? Math.max(6, ratio * 100) : 0,
                    isToday: index === todayIndex,
                };
            });
        },

        percentOf(value, total) {
            if (!total) return 0;
            return Math.min(((value || 0) * 100) / total, 100);
        },

        // "18 hrs", "7.5 hrs" — the target differs per host, so never round it
        // down to whole hours the way convertSecondsToHours used to.
        hoursLabel(seconds) {
            const hours = (seconds || 0) / 3600;
            const rounded = Number.isInteger(hours) ? hours : parseFloat(hours.toFixed(1));
            return `${rounded} ${rounded === 1 ? 'hr' : 'hrs'}`;
        },

        formatNumber(value) {
            return (value || 0).toLocaleString('en-IN');
        },

        formatTime(seconds) {
            seconds = seconds || 0;
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            const secs = seconds % 60;
            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
        },

        // "1h 09m" reads faster than "01:09:01" in a narrow day column.
        compactTime(seconds) {
            if (!seconds) return '—';
            const hrs = Math.floor(seconds / 3600);
            const mins = Math.floor((seconds % 3600) / 60);
            if (hrs) return `${hrs}h ${String(mins).padStart(2, '0')}m`;
            return mins ? `${mins}m` : '<1m';
        },

        updateWorkingTimeFormate() {
            const w = this.CurrentWeekTargetData.working_hours || 0;
            this.workingHoursTime = this.formatTime(w);
        },
        updateRemainingTime() {
            this.remainingTime = this.formatTime(this.remainingSeconds >= 0 ? this.remainingSeconds : 0);
        },

        peak_updateWorkingTimeFormate() {
            const w = this.CurrentWeekTargetData.working_peak_hours || 0;
            this.peak_workingHoursTime = this.formatTime(w);
        },
        peak_updateRemainingTime() {
            this.peak_remainingTime = this.formatTime(this.peak_remainingSeconds >= 0 ? this.peak_remainingSeconds : 0);
        },

        lastWeek_updateWorkingTimeFormate() {
            const w = this.LastWeekTargetData.working_hours || 0;
            this.lastWeek_workingHoursTime = this.formatTime(w);
        },
        lastWeek_updateRemainingTime() {
            this.lastWeek_remainingTime = this.formatTime(this.lastWeek_remainingSeconds >= 0 ? this.lastWeek_remainingSeconds : 0);
        },

        lastWeek_peak_updateWorkingTimeFormate() {
            const w = this.LastWeekTargetData.working_peak_hours || 0;
            this.lastWeek_peak_workingHoursTime = this.formatTime(w);
        },
        lastWeek_peak_updateRemainingTime() {
            this.lastWeek_peak_remainingTime = this.formatTime(this.lastWeek_peak_remainingSeconds >= 0 ? this.lastWeek_peak_remainingSeconds : 0);
        },
    }
};
</script>
